import type { ExperimentPipeline } from 'types';
import type { PipelinesManager } from '@/state/pipelines/usePipelines';
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { UserAvatar } from '@/components/UserAvatar';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';
import { PIPELINE_STATUS_TONES } from './labels';
import { CreatePipelineForm } from './CreatePipelineForm';
import { PipelinesEmptyState } from './PipelinesEmptyState';

type PipelineListProps = {
  /** Owned by PipelinesScreen, which also reads it to decide the page header. */
  manager: PipelinesManager;
  onOpen: (pipelineId: number) => void;
};

export const PipelineList = ({ manager, onOpen }: PipelineListProps) => {
  const { pipelines, isLoading, websiteId, createPipeline, deletePipeline } = manager;

  if (!websiteId) return <p className="supporting">Select a website first.</p>;
  if (isLoading) return <p className="supporting">Loading pipelines…</p>;

  // The first-run screen carries its own create form, so showing the toolbar too
  // would put the same form on screen twice.
  if (pipelines.length === 0) return <PipelinesEmptyState createPipeline={createPipeline} onOpen={onOpen} />;

  return (
    <div className="flex flex-col gap-[14px]">
      {/* Anchored here, not on the form: the first-run screen carries the same form and
          the tour should not start on it. */}
      <section className="bg-panel border-line rounded-panel border p-3" data-tour="pipelines-new">
        <CreatePipelineForm createPipeline={createPipeline} onCreated={onOpen} />
      </section>

      <section className="bg-panel border-line rounded-panel border" data-tour="pipelines-list">
        <div className="border-line flex items-center justify-between border-b px-3 py-2.5">
          <span className="micro-label">Pipelines</span>
          <span className="micro-label tracking-normal">{pipelines.length}</span>
        </div>
        <div className="grid gap-2.5 p-3 md:grid-cols-2 xl:grid-cols-3">
          {pipelines.map((pipeline) => (
            <PipelineCard key={pipeline.id} pipeline={pipeline} onOpen={onOpen} onDelete={deletePipeline} />
          ))}
        </div>
      </section>
    </div>
  );
};

type PipelineCardProps = {
  pipeline: ExperimentPipeline;
  onOpen: (pipelineId: number) => void;
  onDelete: (pipelineId: number) => Promise<boolean>;
};

const PipelineCard = ({ pipeline, onOpen, onDelete }: PipelineCardProps) => {
  const [confirming, setConfirming] = useState(false);
  const stages = pipeline.stages ?? [];
  const cards = stages.reduce((total, stage) => total + (stage.items?.length ?? 0), 0);
  const done = stages.filter((stage) => stage.state === 'complete').length;

  return (
    /*
      The card itself is inert; the name is the button, stretched over the card with
      an ::after overlay, and delete sits above that overlay as a sibling control.

      It was a role="button" div with an onClick and the dialog nested inside — three
      bugs in one shape. ARIA makes a role="button"'s children presentational, so the
      delete button could drop out of the accessibility tree; the card's key handler
      intercepted Enter on the focused delete button; and Radix portals the dialog in
      the DOM but React bubbles its clicks through the REACT tree, so pressing
      "Delete pipeline" (or Cancel) also fired the card's onClick and opened the
      board of a pipeline that had just been deleted — which is why removing the last
      one never showed the first-run screen. The dialog is now a sibling of the card,
      outside every click handler.
    */
    <>
      <div
        className={cn(
          'border-line bg-panel2 hover:border-sel-line hover:bg-hover relative rounded-card border p-3',
          'transition-colors duration-150',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpen(pipeline.id)}
            aria-label={`Open pipeline ${pipeline.name}`}
            title={pipeline.name}
            className={cn(
              'min-w-0 truncate text-left text-body font-medium focus-visible:outline-none',
              'after:rounded-card after:absolute after:inset-0',
              'focus-visible:after:ring-ring focus-visible:after:ring-2',
            )}
          >
            {pipeline.name}
          </button>
          <button
            type="button"
            aria-label={`Delete ${pipeline.name}`}
            title={`Delete ${pipeline.name}`}
            onClick={() => setConfirming(true)}
            // size-7: a 22px target 4px from the card-wide open overlay meant a
            // near-miss opened the board instead of the delete dialog. relative z-10
            // lifts it above the name button's stretched ::after.
            className={cn(
              'text-dim hover:text-danger hover:bg-hover relative z-10 flex size-7 shrink-0 items-center justify-center',
              'rounded-lg transition-colors duration-150',
              FOCUS_RING,
            )}
          >
            <Trash2 className="size-3.5" strokeWidth={1.5} />
          </button>
        </div>

        {pipeline.description && (
          <p className="supporting mt-1 line-clamp-2" title={pipeline.description}>
            {pipeline.description}
          </p>
        )}

        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <span className={cn('micro-label rounded-pill border px-2 py-0.5', PIPELINE_STATUS_TONES[pipeline.status])}>
            {pipeline.status}
          </span>
          {pipeline.automationEnabled ? (
            <span className="micro-label border-ok-line text-ok rounded-pill border px-2 py-0.5">Automation on</span>
          ) : (
            <span className="micro-label border-line rounded-pill border px-2 py-0.5">Dry run</span>
          )}
          {pipeline.autoDeployEnabled && (
            <span className="micro-label border-accent-line text-accent rounded-pill border px-2 py-0.5">
              Auto-deploy
            </span>
          )}
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-2">
          <p className="supporting m-0">
            {stages.length === 0
              ? 'No stages yet'
              : `${stages.length} ${stages.length === 1 ? 'stage' : 'stages'} · ${cards} ${
                  cards === 1 ? 'experiment' : 'experiments'
                }${done > 0 ? ` · ${done} finished` : ''}`}
          </p>
          {/* Who made it. relative z-10 lifts it above the name button's stretched
              ::after, like the delete button, so hovering reaches the tooltip. That also
              takes it out of the card-wide open target, so the click is forwarded: a
              20px dead spot on a surface that otherwise opens anywhere would read as broken. */}
          {!!pipeline.createdByUser && (
            <span className="relative z-10 shrink-0" onClick={() => onOpen(pipeline.id)}>
              <UserAvatar user={pipeline.createdByUser} size="xs" tooltip />
            </span>
          )}
        </div>
      </div>

      {/* The board is the only record of what ran in what order, so deleting one is
          worth a question even though the row is only soft-deleted. */}
      <ConfirmDialog
        open={confirming}
        title={`Delete "${pipeline.name}"?`}
        description="The stages and their history are kept, but the board disappears from this list. Experiments already running are not stopped."
        confirmLabel="Delete pipeline"
        confirmVariant="destructive"
        onConfirm={async () => {
          await onDelete(pipeline.id);
          setConfirming(false);
        }}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
};
